import React from 'react';
import * as yup from 'yup';
import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import Column from '../../../common/layout/Column';
import { ClaimContext } from '../types';
import { ClaimStepContainerProps } from '../common/ClaimStepContainer';
import RichLocale from '../../../common2/RichLocale';
import { useMutation } from '@tanstack/react-query';
import container from '../../../../container';
import LinkPreview from '../../../common/LinkPreview';
import Locale from '../../../Translate';
import Text from '../../../common/Text/Text';
import styles from './PictureUploadLinkStep.scss';
import Button from '../../../common2/Button';
import Heading3 from '../../../common/Heading3';
import ValidationError from '../../../common/ValidationError';
import { ConsumerActionStatus } from '../../../../api/types';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';
import { isSpecialFlow } from '../claimsHelpers';
import { useValidateStep } from './hooks/useValidateStep';

type ClaimContextForPictureUploadLinkStep = Pick<
  ClaimContext,
  'type' | 'shipmentId' | 'consumerActionStatus' | 'carrierCode' | 'carrierCountryCode'
>;

export type PictureUploadLinkStepProps = {
  claim: ClaimContextForPictureUploadLinkStep;
  as: React.ComponentType<ClaimStepContainerProps>;
  shopId: number;
} & ClaimStepContainerProps;

const schema = yup.object({
  consumerActionStatus: yup
    .mixed<ConsumerActionStatus>()
    .nullable()
    .when(['$isSpecialFlow', '$isPictureUploadLinkCreated', '$hasActivePictureUploadLink', '$isDraftMode'], {
      is: (
        isSpecialFlow: boolean,
        isPictureUploadLinkCreated: boolean,
        hasActivePictureUploadLink: boolean,
        isDraftMode: boolean
      ) => isSpecialFlow && !isPictureUploadLinkCreated && !hasActivePictureUploadLink && !isDraftMode,
      then: (schema) =>
        schema
          .oneOf(
            [ConsumerActionStatus.PENDING, ConsumerActionStatus.COMPLETED],
            'GENERATE_PICTURE_UPLOAD_LINK_REQUIRED_ERROR'
          )
          .required('GENERATE_PICTURE_UPLOAD_LINK_REQUIRED_ERROR'),
    }),
});

function PictureUploadLinkStep(props: PictureUploadLinkStepProps) {
  const { claim, as: Container, shopId, ...restProps } = props;

  const { shipmentId, type: claimType, consumerActionStatus, carrierCode, carrierCountryCode } = claim;

  if (!shipmentId || !claimType) {
    throw new Error('The necessary data for the Picture Upload Link step is not available');
  }

  const shopSettings = useClaimsShopSettings();

  const mutation = useMutation({
    mutationKey: ['createPictureUploadLink', shopId, shipmentId, claimType],
    mutationFn: () => container.touchpointsApi.createPictureUploadLink(shopId, shipmentId, claimType),
  });

  const isClaimSpecialFlow = isSpecialFlow({
    shopSettings,
    type: claimType,
    carrierCode,
    carrierCountryCode,
  });
  const hasActiveLink =
    consumerActionStatus === ConsumerActionStatus.PENDING || consumerActionStatus === ConsumerActionStatus.COMPLETED;

  const validationContext = {
    isSpecialFlow: isClaimSpecialFlow,
    isPictureUploadLinkCreated: mutation.isSuccess,
    hasActivePictureUploadLink: hasActiveLink,
  };

  const { onClickNext, onClickBack, onClickSaveDraft, errors } = useValidateStep(
    schema,
    claim,
    validationContext,
    props
  );

  const linkError = !mutation.isSuccess ? errors.consumerActionStatus : undefined;

  return (
    <Container
      {...restProps}
      onNext={onClickNext}
      onBack={onClickBack}
      onSaveDraft={onClickSaveDraft}
      isDisabled={mutation.isLoading}
    >
      <SidePanelContentSection heading="PICTURE_UPLOAD_LINK_SECTION_HEADER" />
      <RichLocale ink>PICTURE_UPLOAD_LINK_SECTION_INFO_RICHTEXT</RichLocale>
      <Column>
        <div className={styles.signingLinkContainer}>
          <Heading3>GENERATE_PICTURE_UPLOAD_LINK_FORM_HEADER</Heading3>
          <Text className="ink">
            <Locale>SEND_PICTURE_UPLOAD_LINK_TO_CUSTOMER</Locale>
          </Text>
          <div className={styles.signingLinkColumnLayout}>
            <div className={linkError ? styles.signingLinkError : styles.signingLink}>
              <LinkPreview
                showCopyButton={mutation.isSuccess}
                isLoading={mutation.isLoading}
                isError={mutation.isError}
                errorText="LINK_PREVIEW_PICTURE_UPLOAD_LINK_ERROR"
                loadingText="LINK_PREVIEW_PICTURE_UPLOAD_LINK_LOADING"
                placeholder="LINK_PREVIEW_PICTURE_UPLOAD_LINK_PLACEHOLDER"
              >
                {mutation.data?.pictureUploadLink}
              </LinkPreview>
            </div>
            <Button
              name="generate-picture-upload-link-button"
              onClick={() => mutation.mutate()}
              size="medium"
              disabled={mutation.isLoading}
            >
              GENERATE_PICTURE_UPLOAD_LINK_BUTTON_TEXT
            </Button>
          </div>
          {linkError && <ValidationError>{linkError}</ValidationError>}
        </div>
      </Column>
    </Container>
  );
}

export default React.memo(PictureUploadLinkStep);
